import { useState, useEffect } from "react";
import { apiService, Service } from "../services/api";
import { useLanguage } from "../contexts/LanguageContext";
import { isPrerendering } from "../utils/prerender";
import {
  fetchWithCache,
  getCached,
  isCacheValid,
  invalidateCacheByPrefix,
} from "../utils/requestCache";

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface UseServicesOptions {
  category?: string;
  popular?: boolean;
  autoFetch?: boolean;
}

interface UseServicesReturn {
  services: Service[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useServices = (
  options: UseServicesOptions = {}
): UseServicesReturn => {
  const { category, popular, autoFetch = true } = options;
  const { currentLanguage } = useLanguage();

  // Build cache key based on all parameters
  const cacheKey = `services_${currentLanguage}_${category || "all"}_${
    popular || "all"
  }`;

  // Initialize from cache if available
  const [services, setServices] = useState<Service[]>(() => {
    return getCached<Service[]>(cacheKey, CACHE_DURATION) || [];
  });
  const [loading, setLoading] = useState<boolean>(
    () => !isCacheValid(cacheKey, CACHE_DURATION)
  );
  const [error, setError] = useState<string | null>(null);

  const fetchServices = async (forceRefresh = false) => {
    if (isPrerendering()) return; // keep the loading skeleton in the snapshot
    if (forceRefresh) {
      invalidateCacheByPrefix("services_");
    }

    setLoading(true);
    setError(null);

    try {
      const data = await fetchWithCache(
        cacheKey,
        () =>
          apiService.getServices({
            category,
            popular,
            lang: currentLanguage,
          }),
        CACHE_DURATION
      );
      setServices(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch services";
      setError(errorMessage);
      console.error("Error fetching services:", err);

      // Fallback to mock data if API fails
      setServices(getMockServices());
    } finally {
      setLoading(false);
    }
  };

  const refetch = async () => {
    await fetchServices(true);
  };

  useEffect(() => {
    let cancelled = false;

    if (autoFetch) {
      fetchServices().then(() => {
        if (cancelled) return;
      });
    }

    return () => {
      cancelled = true;
    };
  }, [category, popular, autoFetch, currentLanguage]);

  return {
    services,
    loading,
    error,
    refetch,
  };
};

// Fallback mock data for development/offline use
const getMockServices = (): Service[] => [
  {
    id: "1",
    name: "Nettoyage Professionnel",
    description:
      "Nettoyage complet avec produits professionnels de haute qualité.",
    shortDescription: "Nettoyage complet avec produits professionnels",
    price: 15.0,
    originalPrice: 20.0,
    duration: 4320,
    durationText: "3-5 jours",
    isActive: true,
    category: "cleaning",
    features: [
      "Nettoyage en profondeur",
      "Désinfection",
      "Séchage professionnel",
      "Collecte à domicile",
    ],
    includesPickup: true,
    popular: false,
    requirements: [],
    limitations: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "2",
    name: "Service Premium",
    description:
      "Service haut de gamme avec traitement spécialisé pour vos chaussures de luxe.",
    shortDescription: "Service haut de gamme avec traitement spécialisé",
    price: 25.0,
    originalPrice: 30.0,
    duration: 8640,
    durationText: "5-7 jours",
    isActive: true,
    category: "luxury",
    features: [
      "Nettoyage premium",
      "Traitement cuir",
      "Protection imperméable",
      "Collecte à domicile",
      "Emballage soigné",
    ],
    includesPickup: true,
    popular: true,
    requirements: [],
    limitations: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "3",
    name: "Service Express",
    description: "Nettoyage rapide en 24h pour vos besoins urgents.",
    shortDescription: "Nettoyage rapide en 24h",
    price: 30.0,
    originalPrice: 35.0,
    duration: 1440,
    durationText: "24 heures",
    isActive: true,
    category: "express",
    features: [
      "Nettoyage express",
      "Séchage rapide",
      "Collecte à domicile",
      "Livraison prioritaire",
    ],
    includesPickup: true,
    popular: false,
    requirements: [],
    limitations: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

export default useServices;
